import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Avatar, IconButton, Button, Divider, Grid } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SendToMobileIcon from "@mui/icons-material/SendToMobile";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PaymentIcon from "@mui/icons-material/Payment";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import { AppColors } from "../../../../../util/AppColors";
import CustomButton from "../../../../../components/CustomButton";
import { FaWhatsapp, FaRegEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import EditClientProfileModal from "./EditClientProfileModal";

const ChatDetailsSidebar = ({
  patientDetails,
  petDetails,
  recentVisits = [],
  invoices = [],
  shortcuts = []
}) => {
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentPatientDetails, setCurrentPatientDetails] = useState(patientDetails || {});

  useEffect(() => {
    if (patientDetails) {
      setCurrentPatientDetails(patientDetails);
    } else {
      setCurrentPatientDetails({});
    }
  }, [patientDetails]);

  const shortcutIcons = {
    send_invoice: <ReceiptIcon sx={{ fontSize: "18px" }} />,
    send_emr: <HistoryEduIcon sx={{ fontSize: "18px" }} />,
    send_lab_report: <AssessmentIcon sx={{ fontSize: "18px" }} />,
    appointment_summary: <EventAvailableIcon sx={{ fontSize: "18px" }} />,
    set_reminder: <SendToMobileIcon sx={{ fontSize: "18px" }} />,
    share_payment_link: <PaymentIcon sx={{ fontSize: "18px" }} />
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid #E2E8F0",
        height: "100%",
        backgroundColor: AppColors.white
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center"
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: "700",
            color: "#1E293B",
            fontFamily: "Montserrat"
          }}
        >
          Details
        </Typography>
      </Box>

      {/* Scrollable Panel Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Patient Information Card */}
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: "1px solid #F1F5F9",
                backgroundColor: "#F8FAFC",
                position: "relative"
              }}
            >
              {/* Header Info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Avatar
                  src={currentPatientDetails?.image || undefined}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: "#0054A6",
                    color: AppColors.white,
                    fontSize: "16px",
                    fontWeight: "700",
                    fontFamily: "Montserrat"
                  }}
                >
                  {currentPatientDetails?.avatarInitials}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "15px",
                      fontWeight: "700",
                      color: "#1E293B",
                      fontFamily: "Montserrat"
                    }}
                  >
                    {currentPatientDetails?.name}
                  </Typography>
                  {/* Client Since Tag */}
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, backgroundColor: "#FFFBEB", color: "#B45309", px: 1, py: 0.25, borderRadius: "6px", border: "1px solid #FEF3C7" }}>
                    <StarIcon sx={{ fontSize: "11px", color: "#D97706" }} />
                    <Typography sx={{ fontSize: "9px", fontWeight: "700", fontFamily: "Montserrat" }}>
                      Client since {currentPatientDetails?.clientSince}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  sx={{ color: AppColors.gray5 }}
                  onClick={() => setEditModalOpen(true)}
                >
                  <EditIcon sx={{ fontSize: "18px" }} />
                </IconButton>
              </Box>

              <Divider sx={{ my: 1.5, borderColor: "#E2E8F0" }} />

              {/* Patient Contacts Info */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FaWhatsapp style={{ color: "#25D366", fontSize: "14px" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: "600", color: "#334155", fontFamily: "Montserrat" }}>
                    {currentPatientDetails?.phone}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FaRegEnvelope style={{ color: "#64748B", fontSize: "13px" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#64748B", fontFamily: "Montserrat" }}>
                    {currentPatientDetails?.email}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FaMapMarkerAlt style={{ color: "#64748B", fontSize: "13px" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#64748B", fontFamily: "Montserrat" }}>
                    {currentPatientDetails?.address}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Pet Profile Card */}
            {petDetails && (
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: "1px solid #F1F5F9",
                backgroundColor: "#F8FAFC"
              }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5, fontFamily: "Montserrat" }}>
                Pet Profile
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    backgroundColor: "#FFC225",
                    color: AppColors.white,
                    fontSize: "16px",
                    fontWeight: "700",
                    fontFamily: "Montserrat"
                  }}
                >
                  {petDetails.avatarInitials}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#1E293B", fontFamily: "Montserrat" }}>
                    {petDetails.name}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", fontWeight: "500", color: "#64748B", fontFamily: "Montserrat", mt: 0.25 }}>
                    {[petDetails.breed, petDetails.gender, petDetails.age].filter(Boolean).join(" | ") || "—"}
                  </Typography>
                  {petDetails.patientId || petDetails.petId ? (
                    <Typography sx={{ fontSize: "10px", fontWeight: "600", color: "#94A3B8", fontFamily: "Montserrat", mt: 0.25 }}>
                      Patient ID: {petDetails.patientId || petDetails.petId}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <CustomButton
                text="View Pet Details"
                tabOutlined
                fullWidth
                onClick={() => navigate("/clinic-pet-details/overview")}
              />
            </Box>
            )}

            {/* Recent Visits Card */}
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: "1px solid #F1F5F9",
                backgroundColor: "#F8FAFC"
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Montserrat" }}>
                  Recent Visits
                </Typography>
                <Typography
                  onClick={() => navigate("/appointments/calendar")}
                  sx={{ fontSize: "11px", fontWeight: "700", color: "#0054A6", cursor: "pointer", fontFamily: "Montserrat" }}
                >
                  View All
                </Typography>
              </Box>
              {recentVisits.map((visit, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor: AppColors.white,
                    border: "1px solid #E2E8F0"
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: "700", color: "#1E293B", fontFamily: "Montserrat" }}>
                      {visit.date} • {visit.type}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: AppColors.gray3, fontFamily: "Montserrat", mt: 0.25 }}>
                      {visit.doctor}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{ backgroundColor: "#EAF8E6", color: "#1E6B08", fontSize: "10px", fontWeight: "700", px: 1, py: 0.25, borderRadius: "6px", fontFamily: "Montserrat" }}>
                      {visit.status}
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: "16px", color: "#94A3B8" }} />
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Invoices Card */}
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                border: "1px solid #F1F5F9",
                backgroundColor: "#F8FAFC"
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Montserrat" }}>
                  Invoices
                </Typography>
                <Typography
                  onClick={() => navigate("/payments/dashboard")}
                  sx={{ fontSize: "11px", fontWeight: "700", color: "#0054A6", cursor: "pointer", fontFamily: "Montserrat" }}
                >
                  View All
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {invoices.map((invoice, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1,
                      borderRadius: "8px",
                      backgroundColor: AppColors.white,
                      border: "1px solid #E2E8F0"
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#0054A6", fontFamily: "Montserrat" }}>
                      {invoice.id}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#64748B", fontFamily: "Montserrat" }}>
                      {invoice.date}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#1E293B", fontFamily: "Montserrat" }}>
                      {invoice.amount}
                    </Typography>
                    <Box sx={{ backgroundColor: "#EAF8E6", color: "#1E6B08", fontSize: "9px", fontWeight: "700", px: 0.8, py: 0.1, borderRadius: "4px", fontFamily: "Montserrat" }}>
                      {invoice.status}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Shortcuts Panel */}
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.5, fontFamily: "Montserrat" }}>
                Shortcuts
              </Typography>
              <Grid container spacing={1.5}>
                {shortcuts.map((shortcut, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={shortcutIcons[shortcut.action]}
                      sx={{
                        textTransform: "none",
                        borderColor: "#E2E8F0",
                        color: "#334155",
                        fontFamily: "Montserrat",
                        fontSize: "11px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        height: "38px",
                        justifyContent: "flex-start",
                        px: 1,
                        backgroundColor: "#F8FAFC",
                        "&:hover": {
                          backgroundColor: "#F1F5F9",
                          borderColor: "#CBD5E1"
                        },
                        "& .MuiButton-startIcon": {
                          mr: 0.5,
                          color: "#64748B"
                        }
                      }}
                    >
                      {shortcut.label}
                    </Button>
                  </Grid>
                ))}
                {/* Full-width templates button */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<HistoryEduIcon sx={{ fontSize: "18px" }} />}
                    sx={{
                      textTransform: "none",
                      borderColor: "#0054A6",
                      color: "#0054A6",
                      fontFamily: "Montserrat",
                      fontSize: "12px",
                      fontWeight: "700",
                      borderRadius: "8px",
                      height: "40px",
                      px: 2,
                      backgroundColor: "#EFF6FC",
                      "&:hover": {
                        backgroundColor: "#E2F0FD",
                        borderColor: "#0054A6"
                      },
                      "& .MuiButton-startIcon": {
                        color: "#0054A6"
                      }
                    }}
                  >
                    Use Message Template
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
      </Box>

      <EditClientProfileModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        patientDetails={currentPatientDetails}
        onSave={(updated) => setCurrentPatientDetails(updated)}
      />
    </Box>
  );
};

export default ChatDetailsSidebar;
